import { DateTime } from 'luxon';
export const DEFAULT_WINDOW_NIGHTS = 7;
const asDate = (iso) => DateTime.fromISO(iso, { zone: 'utc' }).startOf('day');
const iso = (date) => date.toISODate() ?? '';
const nightsBetween = (from, to) => Math.max(Math.round(to.diff(from, 'days').days), 0);
// The number of nights spanned by a range.
export const rangeNights = (range) => nightsBetween(asDate(range.start), asDate(range.end));
// The latest window start such that a full window still fits before the range
// end. When the range is narrower than a window, this is just the range start.
const latestStart = (range, nights) => {
    const start = asDate(range.start);
    const candidate = asDate(range.end).minus({ days: nights });
    return candidate < start ? start : candidate;
};
// Clamps a proposed window start into `[range.start, latestStart]`.
export const clampWindowStart = (startISO, range, nights) => {
    const start = asDate(startISO);
    const min = asDate(range.start);
    const max = latestStart(range, nights);
    if (start < min) {
        return iso(min);
    }
    if (start > max) {
        return iso(max);
    }
    return iso(start);
};
// The visible window for a (clamped) start. The window never extends past the
// range end, so the final page can be shorter than `nights`.
export const windowFrom = (startISO, range, nights) => {
    const from = asDate(clampWindowStart(startISO, range, nights));
    const naiveTo = from.plus({ days: nights });
    const end = asDate(range.end);
    return { from: iso(from), to: iso(naiveTo > end ? end : naiveTo) };
};
// Shifts the window start one page in `direction` (+1 forward, -1 back),
// clamped to the range.
export const pageWindowStart = (currentStart, range, nights, direction) => clampWindowStart(iso(asDate(currentStart).plus({ days: direction * nights })), range, nights);
// The window start that brings today into view, clamped to the range (so a
// today outside the search range lands on the nearest edge).
export const todayWindowStart = (todayISO, range, nights) => clampWindowStart(todayISO, range, nights);
export const canPageBack = (currentStart, range) => asDate(currentStart) > asDate(range.start);
export const canPageForward = (currentStart, range, nights) => asDate(currentStart) < latestStart(range, nights);
// The display parts for a list of ISO dates, in order.
export const toDayColumns = (dates) => dates.map(date => {
    const day = asDate(date);
    return { date, weekday: day.toFormat('ccc'), dayOfMonth: day.toFormat('d') };
});
// "Sep 8–14 of Sep 8–Oct 8": the visible window (inclusive of its last night)
// against the wider search range.
export const formatWindowLabel = (window, range) => {
    const from = asDate(window.from);
    const lastNight = asDate(window.to).minus({ days: 1 });
    const endLabel = lastNight.hasSame(from, 'month')
        ? lastNight.toFormat('d')
        : lastNight.toFormat('LLL d');
    const windowLabel = `${from.toFormat('LLL d')}–${endLabel}`;
    const rangeLabel = `${asDate(range.start).toFormat('LLL d')}–${asDate(range.end).toFormat('LLL d')}`;
    return `${windowLabel} of ${rangeLabel}`;
};
